import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router';
import { TokenContext } from '../Context/TokenContext';
import { getSettings, updateSettings } from '../Utils/settingsApi';

const LEGAL_PAGES = [
    { path: '/settings/mentions-legales', label: 'Mentions légales', secondary: 'Un seul document, valable pour tout le site' },
    { path: '/settings/cgv', label: 'Conditions générales de vente', secondary: 'Un seul document, valable pour tout le site' },
    { path: '/settings/cgu', label: "Conditions générales d'utilisation", secondary: 'Un document par logiciel' },
];

export default function SettingsPage() {
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [defaultManagementFeeLabel, setDefaultManagementFeeLabel] = useState('');

    const fetchSettings = useCallback(() => {
        if (!accessToken) return;
        setLoading(true);
        getSettings(accessToken)
            .then((data) => {
                setDefaultManagementFeeLabel(data.default_management_fee_label || '');
            })
            .catch(() => setError('Impossible de charger les réglages'))
            .finally(() => setLoading(false));
    }, [accessToken]);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleSave = () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        updateSettings(accessToken, {
            default_management_fee_label: defaultManagementFeeLabel,
        })
            .then(() => setSaved(true))
            .catch(() => setError('Échec de l\'enregistrement'))
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    Réglages
                </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Export PDF des fiches produit</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Valeur pré-remplie dans la boîte de dialogue "Exporter en PDF" (prestations et
                    logiciels) lorsque le produit n'a pas déjà sa propre note de frais. Modifiable
                    au cas par cas à chaque export, sans impact sur le site public.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Réglages enregistrés.</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                ) : (
                    <Stack spacing={2}>
                        <TextField
                            label="Frais de gestion par défaut"
                            placeholder="Ex : Frais de dossier 15 %"
                            value={defaultManagementFeeLabel}
                            onChange={(e) => setDefaultManagementFeeLabel(e.target.value)}
                            size="small"
                            fullWidth
                        />
                        <Box>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ maxWidth: 560, mt: 3 }}>
                <Box sx={{ p: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Contenu légal (site public)</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Chaque document a son propre éditeur (titre, sous-titre, contenu).
                    </Typography>
                </Box>
                <List disablePadding>
                    {LEGAL_PAGES.map(({ path, label, secondary }) => (
                        <ListItemButton key={path} divider onClick={() => navigate(path)}>
                            <ListItemText primary={label} secondary={secondary} />
                            <ChevronRightIcon color="disabled" />
                        </ListItemButton>
                    ))}
                </List>
            </Paper>
        </Box>
    );
}
